import type { LockCodeEntry, LockCodeRail } from '@citibox-panel/api-client';

export interface LockCodeSlotCounts {
  /** Small slots left of the divider — the codes already given. */
  given: number;
  /** Small slots right of the big «SIGUIENTE» slot. */
  future: number;
}

export interface LockCodeWindow {
  /** Oldest→newest, always `slots.given` long; `null` where the read's window does not reach. */
  given: (LockCodeEntry | null)[];
  /** The big slot: the candidate «siguiente a dar» at the current offset. */
  next: LockCodeEntry | null;
  /** Always `slots.future` long, same padding rule as `given`. */
  future: (LockCodeEntry | null)[];
  canSlideBack: boolean;
  canSlideForward: boolean;
  /**
   * Signed distance from the server pointer, in codes: 0 is server state, negative walks back
   * through the codes already given, positive into the ones not given yet. The EFFECTIVE distance
   * (after clamping), because it is what the UI counts out beside the labels — a strip that could
   * only take three of the twenty steps asked for has moved three.
   */
  steps: number;
}

// Runs a carril's sequence through the FIXED slots of the strip: the slots never move, the codes
// move through them. `offset` is how far the agent has slid from the server pointer — 0 is server
// state, negative walks back through the codes already given, positive walks into the future ones.
//
// The anchor is found by INDEX in the sequence the read returned, never by arithmetic on the
// position numbers: the table wraps forward past its end (…399, 0, 1, 2), so `currentPosition + 1`
// is not the next entry on the very locks nearest the wrap.
//
// Returns `null` when there is nothing to paint — a carril with no codes, an empty window, or a
// pointer the read did not include (which contradicts the contract; better «No hay códigos
// disponibles» than anchoring the big slot on an arbitrary code the agent would read out loud).
// Slots that fall outside the returned window stay empty: the read's edge is the CC↔Maintenance
// boundary, so the strip stops there instead of paginating.
export function buildLockCodeWindow(
  rail: LockCodeRail | null,
  offset: number,
  slots: LockCodeSlotCounts,
): LockCodeWindow | null {
  if (rail === null || rail.sequence.length === 0) return null;
  const pointerIndex = rail.sequence.findIndex((entry) => entry.position === rail.currentPosition);
  if (pointerIndex === -1) return null;

  // The UI disables the control at the edge, so an out-of-range offset should be unreachable;
  // clamping keeps a stray one from emptying the whole strip.
  const lastIndex = rail.sequence.length - 1;
  const anchor = Math.min(Math.max(pointerIndex + offset, 0), lastIndex);
  const at = (index: number): LockCodeEntry | null =>
    index >= 0 && index <= lastIndex ? (rail.sequence[index] ?? null) : null;

  return {
    given: Array.from({ length: slots.given }, (_, i) => at(anchor - slots.given + i)),
    next: at(anchor),
    future: Array.from({ length: slots.future }, (_, i) => at(anchor + 1 + i)),
    canSlideBack: anchor > 0,
    canSlideForward: anchor < lastIndex,
    steps: anchor - pointerIndex,
  };
}
